/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/display-name */

import React from 'react';
import { TableComponents, TableVirtuoso } from 'react-virtuoso';
import { Paper, Table, TableBody, TableCell, TableContainer, TableRow, TableHead, useTheme, CircularProgress } from '@mui/material';
//import useScreenSize from 'common/hooks/useScreenSize';
import OrderDataTableProps from 'common/types/OrderDataTableProps';
import statusList from 'common/data/statusList';
import StatusLabel from 'common/components/StatusLabel';
import { useNavigate } from 'react-router-dom';



interface ColumnData {
  dataKey: keyof OrderDataTableProps;
  label: string;
  numeric?: boolean;
  width: number;
  isName?: boolean;
}

const columns: ColumnData[] = [
  {
    width: 247 / 2,
    label: 'Прийняття / Видача',
    dataKey: 'acceptanceDate',
  },
  {
    width: 106 / 2,
    label: 'Номер',
    dataKey: 'id',
    isName: true,
  },
  {
    width: 112 / 2,
    label: 'Партнер',
    dataKey: 'isPartner',
  },
  {
    width: 137 / 2,
    label: 'Статус',
    dataKey: 'orderStatus',
  },
];

/*const rows: UserDataProps[] = Array.from({ length: 100 }, () => {
  const randomSelection = sample[Math.floor(Math.random() * sample.length)];
  return createData(...randomSelection);
});*/

const VirtuosoTableComponents: TableComponents<OrderDataTableProps> = {
  Scroller: React.forwardRef<HTMLDivElement>((props, ref) => (
    <TableContainer sx={{
      '&::-webkit-scrollbar': {
        width: '14px',
        padding: '15px 0',
        transform: 'scale(0.5)',
      },
      '&::-webkit-scrollbar-track': {
        background: 'rgba(0, 0, 0, 0)',
      },
      '&::-webkit-scrollbar-corner': {
        background: 'rgba(0, 0, 0, 0)',
      },
      '&::-webkit-scrollbar-thumb': {
        background: 'rgba(64, 108, 255, 0.10)',
        borderRadius: '20px',
        border: '3px solid rgba(0, 0, 0, 0)',
        backgroundClip: 'padding-box',
      },
    }} component={Paper} {...props} ref={ref} />
  )),
  Table: (props) => (
    <Table {...props} sx={{ borderCollapse: 'separate', tableLayout: 'fixed' }} />
  ),
  TableHead,
  // eslint-disable-next-line react/prop-types
  TableRow: ({ item: _item, ...props }) => <TableRow sx={{
    height: '72px',
    maxHeight: '72px',
    cursor: 'pointer',
    '& td': {
      transition: 'background-color .2s ease-in-out',
    },
    '&:hover td': {
      backgroundColor: 'rgba(86, 120, 255, 0.25)',
    },
  }} {...props} />,
  TableBody: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
    <TableBody {...props} ref={ref} />
  )),
};

function fixedHeaderContent() {
  const theme = useTheme();
  return (
    <TableRow sx={{ backgroundColor: theme.colors.secondaryColor }}>
      {columns.map((column) => (
        <TableCell
          key={column.dataKey}
          variant="head"
          align={column.numeric || false ? 'right' : 'left'}
          style={{
            width: column.width,
            fontSize: '20px',
            fontStyle: 'normal',
            fontWeight: '500',
            padding: '0px',
            height: '36px',
            verticalAlign: 'top',
            lineHeight: '100%',
            letterSpacing: '1px',
            color: theme.colors.black,
          }}
          className='table-header-cell'
          sx={{
            backgroundColor: 'transparent',
            borderBottom: 'none',
            textAlign: 'center',
            '&:not(:last-of-type)': {
              borderRight: `1px solid ${theme.colors.gray}`,
            },
            '&:first-of-type .table-head-cell': {
              width: 'calc(100% - 8px)',
              marginLeft: '8px',

            },
            '&:last-of-type .table-head-cell': {
              width: 'calc(100% - 8px)',
            },
          }}
        >
          <div style={{ borderColor: theme.colors.gray }} className='table-head-cell'>
            {column?.label}
          </div>
        </TableCell>
      ))}
    </TableRow>
  );
}



type Props = {
  searchValue?: string;
  data: OrderDataTableProps[];
  loading?: boolean;
};

export default function CustomTable({ searchValue, data, loading }: Props) {
  const theme = useTheme();
  const navigate = useNavigate();
  //const screenSize = useScreenSize();
  // Filter data by full name, by number and email
  const filteredData = data.filter((row) => {
    const id = row.id;
    const search = searchValue?.toLowerCase() || '';
    return id.toString().toLowerCase().includes(search || '');
  });

  function rowContent(_index: number, row: OrderDataTableProps) {
    return (
      <React.Fragment>
        {columns.map((column) => {
          let result: number | string | boolean | React.ReactNode = row[column.dataKey];
          if (column.dataKey === 'acceptanceDate') {
            const acceptanceDate = row?.acceptanceDate?.split('T')[0]?.split('-').reverse().join('/');
            result = acceptanceDate;
          }
          // write price with currency and with float .00
          if ( column.dataKey === 'price' ) {
            result = row.price + '₴';
          }
          if (result === undefined || result === null || result === '') {
            result = '—';
            if (column.dataKey === 'acceptanceDate' && row.issueDate?.length > 0) {
              const issueDate = row?.issueDate?.split('T')[0]?.split('-').reverse().join('/');
              result = issueDate;
            }
          }
          // Change 2000-01-01T00:00:00 to 01/01/2000
  
          let resultJSX = <span>{result}</span>;
  
          if (column.dataKey === 'acceptanceDate' && row.acceptanceDate && row.issueDate) {
            // from Date type to dd/mm/yyyy
            const acceptanceDate = row?.acceptanceDate?.split('T')[0]?.split('-').reverse().join('/');
            const issueDate = row?.issueDate?.split('T')[0]?.split('-').reverse().join('/');
            resultJSX = (
              <div className='table-multiline'>
                <span>{acceptanceDate}</span>
                <span>{issueDate}</span>
              </div>
            );
          }
  
          if (column.dataKey === 'orderStatus') {
            const statusText = statusList.find((status) => status.value === row.orderStatus)?.label;
            const statusColor = statusList.find((status) => status.value === row.orderStatus)?.color;
            resultJSX = <StatusLabel status={statusText || ''} color={statusColor} />;
          }
  
          return (
            <TableCell
              key={column.dataKey}
              align={column.numeric || false ? 'right' : 'left'}
              className='table-cell'
              onClick={() => {navigate(row.id.toString());}}
              sx={{
                color: theme.colors.black,
                borderBottom: 'none',
                verticalAlign: 'top',
                padding: '0',
                textAlign: 'center',
                '&:not(:last-of-type)': {
                  borderRight: `1px solid ${theme.colors.gray}`,
                },
                '&:first-of-type': {
                  paddingLeft: '8px',
                  borderTopLeftRadius: '8px',
                  borderBottomLeftRadius: '8px',
                },
                '&:last-of-type': {
                  paddingRight: '8px',
                  borderTopRightRadius: '8px',
                  borderBottomRightRadius: '8px',
                },
                '&:first-of-type .table-cell-border': {
                  justifyContent: 'center',
                },
              }}
            >
              <div style={{ borderColor: theme.colors.gray }} className='table-cell-border'>
                {resultJSX}
              </div>
            </TableCell>
          );
        })}
      </React.Fragment>
    );
  }

  return (
    <Paper sx={{
      backgroundColor: 'transparent',
      boxShadow: 'unset',
      overflow: 'hidden',
      padding: '16px 8px',
      color: theme.colors.black,
      position: 'relative',

    }} style={{ flex: 1 }}>
      {!loading ? <TableVirtuoso
        data={filteredData}
        style={{ boxShadow: 'unset', backgroundColor: 'transparent', color: theme.colors.black }}
        components={VirtuosoTableComponents}
        fixedHeaderContent={fixedHeaderContent}
        itemContent={rowContent}
        onClick={(e)=> {console.log(e);}}
      /> : <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      }}><CircularProgress sx={{

      }} /></div>}
    </Paper>
  );
}